import React from 'react';
import ReactMarkdown from 'react-markdown';
import { User, Bot, ArrowLeft, ArrowUp, ArrowDown, Trash2, Copy, Download } from 'lucide-react';
import { StreamingCodeBlock } from '../common/StreamingCodeBlock';
import { mergeHtmlFences } from '../../lib/htmlChunkAssembler';

type Message = {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  input_tokens?: number;
  output_tokens?: number;
  input_money?: number;
  output_money?: number;
  total_money?: number;
  // 流式消息相关：
  reasoningText?: string;
  isThinking?: boolean;
  partial?: boolean;
  partialReason?: string;
};

type Props = {
  msg: Message;
  index: number;
  onAddToDraft: (content: string) => void;
  onDeletePair: (index: number, id?: number) => void;
  onDeleteSingle: (index: number, id?: number) => void;
  isCurrentUserOwner?: boolean;
  onExport?: (content: string) => void;
};

type ProcessedPart =
  | { type: 'text'; content: string }
  | { type: 'code'; content: string; language: string; isTruncated: boolean };

// 解析内容：完整代码块匹配 + 截断 markdown 检测 + 裸 HTML 检测
// 确保即使多轮接收 HTML 代码也能完整放入一个 <pre><code></code></pre> 中
const processContent = (content: string): ProcessedPart[] => {
  const parts: ProcessedPart[] = [];
  if (!content) return parts;

  // 合并多轮 HTML 代码围栏，确保渲染为单个 <pre><code> 块
  const mergedContent = mergeHtmlFences(content);

  let lastIndex = 0;
  const completeBlockRegex = /```(\w+)?\s*([\s\S]*?)```/g;
  let match: RegExpExecArray | null;

  while ((match = completeBlockRegex.exec(mergedContent)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: mergedContent.substring(lastIndex, match.index) });
    }
    const lang = match[1] || 'text';
    const code = match[2].trim();
    parts.push({ type: 'code', content: code, language: lang, isTruncated: false });
    lastIndex = completeBlockRegex.lastIndex;
  }

  const remaining = mergedContent.substring(lastIndex);
  const unclosedMarkdownMatch = /```(\w+)?\s*([\s\S]*)$/.exec(remaining);
  const rawHtmlStartIdx = remaining.indexOf('<!DOCTYPE html>');
  const isRawHtmlTruncated = rawHtmlStartIdx !== -1 && !remaining.includes('</html>');

  if (unclosedMarkdownMatch) {
    const preText = remaining.substring(0, unclosedMarkdownMatch.index);
    if (preText) parts.push({ type: 'text', content: preText });

    const lang = unclosedMarkdownMatch[1] || 'html';
    const code = unclosedMarkdownMatch[2];
    parts.push({ type: 'code', content: code, language: lang, isTruncated: true });
  } else if (isRawHtmlTruncated) {
    const preText = remaining.substring(0, rawHtmlStartIdx);
    if (preText) parts.push({ type: 'text', content: preText });

    const code = remaining.substring(rawHtmlStartIdx);
    parts.push({ type: 'code', content: code, language: 'html', isTruncated: true });
  } else if (remaining) {
    parts.push({ type: 'text', content: remaining });
  }

  return parts;
};

export const ChatMessage: React.FC<Props> = ({ msg, index, onAddToDraft, onDeletePair, onDeleteSingle, isCurrentUserOwner = false, onExport }) => {
  const isUser = msg.role === 'user';

  // 复制消息内容到剪贴板
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(msg.content);
      alert('消息内容已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  // 解析内容为文本和代码块部分
  const parts = processContent(msg.content);
  const hasContent = msg.content.length > 0;

  return (
    <div className={`flex gap-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? 'bg-gray-200' : 'bg-brand-100 text-brand-600'}`}>
        {isUser ? <User size={16}/> : <Bot size={16}/>}
      </div>
      <div className={`group relative max-w-[85%] p-3 rounded-lg text-sm leading-relaxed shadow-sm ${isUser ? 'bg-gray-100 text-gray-800' : 'bg-brand-50 text-gray-800'}`}>
        <div className="whitespace-pre-wrap">
          {isUser ? (
            msg.content
          ) : (
            <>
              {/* 📝 正文渲染：文本用 ReactMarkdown，代码块用 StreamingCodeBlock */}
              {hasContent ? (
                parts.map((part, i) => {
                  if (part.type === 'code') {
                    return (
                      <StreamingCodeBlock
                        key={i}
                        code={part.content}
                        language={part.language}
                        isTruncated={part.isTruncated}
                        theme="light"
                      />
                    );
                  }
                  return <ReactMarkdown key={i}>{part.content}</ReactMarkdown>;
                })
              ) : (
                <div className="flex items-center justify-center py-2">
                  <svg width="60" height="60" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <style>
    {`
      .body-bounce { animation: bounce 0.3s infinite alternate ease-in-out; }
      .leg-spin { animation: spin 0.2s infinite linear; }
      .sweat-fly { animation: fly 0.6s infinite linear; }
      .eye-blink { animation: blink 2s infinite; }
      @keyframes bounce { from { transform: translateY(0); } to { transform: translateY(-5px); } }
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes fly {
        0% { opacity: 0; transform: translate(0, 0) scale(0.5); }
        50% { opacity: 1; }
        100% { opacity: 0; transform: translate(-20px, -10px) scale(0); }
      }
      @keyframes blink { 0%, 90%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
    `}
  </style>
  {/* 汗水粒子 */}
  <path d="M80 30 Q 85 30 85 25 Q 85 20 80 20 Q 75 20 75 25 Q 75 30 80 30" fill="#60A5FA" className="sweat-fly" style={{animationDelay: '0s', transformOrigin: 'center'}} />
  <path d="M85 45 Q 90 45 90 40 Q 90 35 85 35 Q 80 35 80 40 Q 80 45 85 45" fill="#60A5FA" className="sweat-fly" style={{animationDelay: '0.2s', transformOrigin: 'center'}} />
  {/* 主体容器 */}
  <g className="body-bounce">
    {/* 身体 */}
    <ellipse cx="50" cy="50" rx="30" ry="25" fill="#3B82F6" />
    {/* 眼睛 (带眨眼效果) */}
    <g className="eye-blink" style={{transformOrigin: '50px 45px'}}>
      <circle cx="42" cy="45" r="8" fill="white" />
      <circle cx="42" cy="45" r="3" fill="black" />
      <circle cx="65" cy="45" r="8" fill="white" />
      <circle cx="65" cy="45" r="3" fill="black" />
    </g>
    {/* 嘴巴 (气喘吁吁) */}
    <ellipse cx="55" cy="60" rx="5" ry="8" fill="#1E40AF" />
  </g>
  {/* 腿部风火轮 (残影效果) */}
  <g transform="translate(50, 80)">
    <g className="leg-spin">
      <rect x="-4" y="-15" width="8" height="30" rx="4" fill="#2563EB" opacity="0.8" />
      <rect x="-4" y="-15" width="8" height="30" rx="4" fill="#2563EB" transform="rotate(60)" opacity="0.6" />
      <rect x="-4" y="-15" width="8" height="30" rx="4" fill="#2563EB" transform="rotate(120)" opacity="0.4" />
    </g>
  </g>
  {/* 地面阴影 */}
  <ellipse cx="50" cy="92" rx="20" ry="4" fill="#000" opacity="0.2">
    <animate attributeName="rx" values="20;15;20" dur="0.3s" repeatCount="indefinite" />
  </ellipse>
</svg>

                </div>
              )}

              {/* ⚠️ 内容不完整提示（meta.partial === true） */}
              {msg.partial === true && (
                <div className="mt-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700 flex items-center gap-2">
                  <span>⚠️</span>
                  <span>内容可能不完整{msg.partialReason ? `（${msg.partialReason}）` : ''}</span>
                </div>
              )}
            </>
          )}
        </div>

        {!isUser && ((msg.input_tokens ?? 0) > 0 || (msg.output_tokens ?? 0) > 0 || (msg.total_money ?? 0) > 0 || ((msg.input_money ?? 0) + (msg.output_money ?? 0)) > 0) && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400 font-mono flex items-center gap-1 flex-wrap">
            <span title="input_token" className="inline-flex items-center gap-0.5"><ArrowUp size={10} className="text-green-500" />{msg.input_tokens ?? 0}</span>
            {' / '}
            <span title="output_token" className="inline-flex items-center gap-0.5"><ArrowDown size={10} className="text-orange-500" />{msg.output_tokens ?? 0}</span>
            {' / '}
            <span title="pay">💰¥{((msg.input_money ?? 0) + (msg.output_money ?? 0)).toFixed(4)}</span>
            {' / '}
            <span title="total_money">🏦¥{(msg.total_money ?? 0).toFixed(4)}</span>
          </div>
        )}

        {!isUser && (
          <div className="absolute -right-16 bottom-0 flex flex-col gap-1 opacity-100">
              <button
                onClick={() => onAddToDraft(msg.content)}
                disabled={!isCurrentUserOwner}
                className="p-1.5 text-gray-400 hover:text-brand-600 bg-white border rounded shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Copy to Draft"
              >
                  <ArrowLeft size={14} className="rotate-180"/>
              </button>
              <button
                onClick={() => {
                  if (msg.id) {
                    onDeletePair(index, msg.id);
                  } else {
                    alert("消息正在处理中，请稍候再试");
                  }
                }}
                disabled={!isCurrentUserOwner || !msg.id}
                className="p-1.5 text-gray-400 hover:text-red-600 bg-white border rounded shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={msg.id ? "Delete Pair" : "消息正在处理中"}
              >
                  <Trash2 size={14}/>
              </button>
              <button
                onClick={handleCopyMessage}
                className="p-1.5 text-gray-400 hover:text-blue-600 bg-white border rounded shadow-sm transition-colors"
                title="Copy Message"
              >
                  <Copy size={14}/>
              </button>
              <button
                onClick={() => onExport?.(msg.content)}
                className="p-1.5 text-gray-400 hover:text-green-600 bg-white border rounded shadow-sm transition-colors"
                title="导出为TXT文件"
              >
                  <Download size={14}/>
              </button>
          </div>
        )}

        {isUser && (
          <div className="absolute -left-16 bottom-0 flex flex-col gap-1 opacity-100">
              <button
                onClick={handleCopyMessage}
                className="p-1.5 text-gray-400 hover:text-blue-600 bg-white border rounded shadow-sm transition-colors"
                title="Copy Message"
              >
                  <Copy size={14}/>
              </button>
              <button
                onClick={() => {
                  if (msg.id) {
                    onDeleteSingle(index, msg.id);
                  } else {
                    alert("消息正在处理中，请稍候再试");
                  }
                }}
                disabled={!isCurrentUserOwner || !msg.id}
                className="p-1.5 text-gray-400 hover:text-red-600 bg-white border rounded shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={msg.id ? "Delete Message" : "消息正在处理中"}
              >
                  <Trash2 size={14}/>
              </button>
          </div>
        )}
      </div>
    </div>
  );
};
